import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as VisibilityIcon,
  TrendingUp as PromotionIcon,
  TrendingDown as RelegationIcon,
  SwapHoriz as TransferIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Season,
  Division,
  Player,
  PlayerSeasonTransition,
  PlayerStats,
} from "../types";
import { apiRequest } from "../config/api";
import { useSeason } from "../contexts/SeasonContext";
import {
  calculatePromotionsRelegations,
  getTransitionSummary,
  PromotionRelegationResult,
} from "../utils/seasonTransition";

const DIVISIONS: Division[] = [
  "Master",
  "Division 1",
  "Division 2",
  "Division 2A",
  "Division 2B",
  "Division 3",
  "Division 4",
  "Women Division",
  "Women Division 2",
];

function SeasonManagement() {
  const { allSeasons, activeSeason, refreshSeasons } = useSeason();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Create season dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState("");
  const [newSeasonId, setNewSeasonId] = useState("");
  const [newSeasonStartDate, setNewSeasonStartDate] = useState("");
  const [newSeasonEndDate, setNewSeasonEndDate] = useState("");
  const [newSeasonDivisions, setNewSeasonDivisions] = useState<Division[]>(DIVISIONS);

  // Complete season dialog
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [seasonToComplete, setSeasonToComplete] = useState<Season | null>(null);

  // Edit season dates dialog
  const [editDatesSeason, setEditDatesSeason] = useState<Season | null>(null);
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editDivisions, setEditDivisions] = useState<string[]>([]);

  const toDateInput = (value: any) => {
    if (!value) return "";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
  };

  const openEditDates = (season: Season) => {
    setEditDatesSeason(season);
    setEditStartDate(toDateInput(season.startDate));
    setEditEndDate(toDateInput(season.endDate));
    setEditDivisions((season.divisions as string[]) || []);
  };

  const handleSaveDates = async () => {
    if (!editDatesSeason) return;
    if (!editStartDate || !editEndDate) {
      setError("Start and end date are required");
      return;
    }
    if (editEndDate < editStartDate) {
      setError("End date must be after start date");
      return;
    }
    if (editDivisions.length === 0) {
      setError("Please select at least one division for this season");
      return;
    }
    setLoading(true);
    try {
      const response = await apiRequest(`/api/seasons/${editDatesSeason.id}/dates`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          startDate: editStartDate,
          endDate: editEndDate,
          divisions: editDivisions,
        }),
      });
      if (response.ok) {
        setSuccess("Season updated!");
        setEditDatesSeason(null);
        refreshSeasons();
      } else {
        const data = await response.json();
        setError(data.error || "Failed to update season dates");
      }
    } catch (error) {
      console.error("Error updating season dates:", error);
      setError("Failed to update season dates");
    } finally {
      setLoading(false);
    }
  };

  // Delete draft season dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [seasonToDelete, setSeasonToDelete] = useState<Season | null>(null);

  // Start season wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [seasonToStart, setSeasonToStart] = useState<Season | null>(null);
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [transitionPreview, setTransitionPreview] = useState<
    PlayerSeasonTransition[]
  >([]);
  const [previewGenerated, setPreviewGenerated] = useState(false);
  const [autoTransitions, setAutoTransitions] = useState<
    PromotionRelegationResult[]
  >([]);
  const [manualOverrides, setManualOverrides] = useState<
    Record<string, Division>
  >({});
  const [rankingsByDivision, setRankingsByDivision] = useState<
    Partial<Record<Division, PlayerStats[]>>
  >({});
  const [registeredPlayerIds, setRegisteredPlayerIds] = useState<string[]>([]);
  // Divisions assigned in the registrations tab (playerId -> division; Trial Day -> unassigned)
  const [registrationDivisions, setRegistrationDivisions] = useState<Record<string, string>>({});
  const [useRegistrationDivisions, setUseRegistrationDivisions] = useState(false);

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const response = await apiRequest("/api/players");
      if (response.ok) {
        const data = await response.json();
        setAllPlayers(data);
      }
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const handleCreateSeason = async () => {
    if (
      !newSeasonName.trim() ||
      !newSeasonId.trim() ||
      !newSeasonStartDate ||
      !newSeasonEndDate
    ) {
      setError("Season name, ID, start date, and end date are required");
      return;
    }
    if (newSeasonDivisions.length === 0) {
      setError("Please select at least one division for this season");
      return;
    }

    setLoading(true);
    try {
      const response = await apiRequest("/api/seasons", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newSeasonName.trim(),
          divisions: newSeasonDivisions,
          startDate: newSeasonStartDate,
          endDate: newSeasonEndDate,
        }),
      });

      if (response.ok) {
        setSuccess("Season created successfully!");
        setCreateDialogOpen(false);
        setNewSeasonName("");
        setNewSeasonId("");
        setNewSeasonStartDate("");
        setNewSeasonEndDate("");
        setNewSeasonDivisions(DIVISIONS);
        refreshSeasons();
      } else {
        const data = await response.json();
        setError(data.error || "Failed to create season");
      }
    } catch (error) {
      console.error("Error creating season:", error);
      setError("Failed to create season");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSeason = async () => {
    if (!seasonToComplete) return;

    setLoading(true);
    try {
      const response = await apiRequest(
        `/api/seasons/${seasonToComplete.id}/complete`,
        {
          method: "PUT",
        },
      );

      if (response.ok) {
        setSuccess("Season completed successfully!");
        setCompleteDialogOpen(false);
        setSeasonToComplete(null);
        refreshSeasons();
      } else {
        const data = await response.json();
        setError(data.error || "Failed to complete season");
      }
    } catch (error) {
      console.error("Error completing season:", error);
      setError("Failed to complete season");
    } finally {
      setLoading(false);
    }
  };

  const handleStartSeasonWizard = async (season: Season) => {
    setSeasonToStart(season);
    setWizardOpen(true);
    setActiveStep(0);
    setTransitionPreview([]);
    setPreviewGenerated(false);
    setManualOverrides({});
    setUseRegistrationDivisions(false);
    setRegistrationDivisions({});

    // Fetch all active players
    const activePlayers = allPlayers.filter((p) => p.isActive);
    
    // Backfill any approved registrations missing a playerId (one-time fix for legacy data)
    try {
      await apiRequest("/api/season-registration/backfill-player-ids", { method: "POST" });
    } catch (e) {
      console.warn("Backfill playerId failed (non-fatal):", e);
    }

    // Fetch registered players for the new season
    try {
      const response = await apiRequest(`/api/season-registration/all?seasonId=${encodeURIComponent(season.id)}`);
      if (response.ok) {
        const registrations = await response.json();
        // Get player IDs from approved registrations — covers both:
        // - existing players (isExistingPlayer: true, playerId set at registration time)
        // - new players whose account was created on approval (playerId written back by server)
        const registeredIds = registrations
          .filter((r: any) => 
            r.status === 'approved' && 
            r.playerId
          )
          .map((r: any) => r.playerId);
        
        setRegisteredPlayerIds(registeredIds);

        const regDivisions: Record<string, string> = {};
        registrations.forEach((r: any) => {
          if (r.status === 'approved' && r.playerId && r.assignedDivision) {
            regDivisions[r.playerId] = r.assignedDivision === 'Trial Day' ? 'unassigned' : r.assignedDivision;
          }
        });
        setRegistrationDivisions(regDivisions);

        // Pre-select only registered players; unregistered remain unchecked
        const playersToSelect = activePlayers
          .filter(p => registeredIds.includes(p.id))
          .map(p => p.id);
        
        setSelectedPlayers(playersToSelect);
      } else {
        // Fallback: select all active players if can't fetch registrations
        setSelectedPlayers(activePlayers.map((p) => p.id));
        setRegisteredPlayerIds([]);
      }
    } catch (error) {
      console.error("Error fetching registrations:", error);
      // Fallback: select all active players
      setSelectedPlayers(activePlayers.map((p) => p.id));
      setRegisteredPlayerIds([]);
    }

    // Fetch rankings for all divisions from current season
    if (activeSeason) {
      await fetchRankingsForAllDivisions(activeSeason.id);
    }
  };

  const fetchRankingsForAllDivisions = async (seasonId: string) => {
    const rankings: Partial<Record<Division, PlayerStats[]>> = {};

    for (const division of DIVISIONS) {
      try {
        const params = new URLSearchParams({
          division,
          seasonId,
        });
        const response = await apiRequest(
          `/api/stats/rankings?${params.toString()}`,
        );
        if (response.ok) {
          const data = await response.json();
          rankings[division] = data;
        }
      } catch (error) {
        console.error(`Error fetching rankings for ${division}:`, error);
      }
    }

    setRankingsByDivision(rankings);

    // Calculate automatic transitions
    const allAutoTransitions: PromotionRelegationResult[] = [];
    for (const division of DIVISIONS) {
      if (rankings[division]) {
        // Filter out guest players - only include players registered in this division
        const registeredPlayersOnly = rankings[division]!.filter(
          (player) => player.division === division && !player.isGuestPlayer,
        );
        const transitions = calculatePromotionsRelegations(
          registeredPlayersOnly,
          division,
        );
        allAutoTransitions.push(...transitions);
      }
    }
    setAutoTransitions(allAutoTransitions);
  };

  // Selections that bypass auto promotions/relegations: the division assigned in the
  // registrations tab wins, a manual override beats it, and players without a registration
  // division fall back to their auto transition (or stay where they are).
  const buildRegistrationSelections = () =>
    selectedPlayers.map((playerId) => {
      const auto = autoTransitions.find((t) => t.playerId === playerId);
      const player = allPlayers.find((p) => p.id === playerId);
      const currentDivision = auto?.currentDivision || player?.division || "unassigned";
      const manualDivision = manualOverrides[playerId];
      const regDivision = registrationDivisions[playerId];
      const newDivision = manualDivision || regDivision || auto?.newDivision || currentDivision;
      const transitionType = manualDivision || regDivision
        ? (newDivision === currentDivision ? "stays" : "manual")
        : auto?.transitionType || "stays";
      return {
        playerId,
        playerName: auto?.playerName || player?.name || playerId,
        currentDivision,
        newDivision,
        transitionType,
        shouldContinue: true,
      };
    });

  const handlePreviewTransitions = async () => {
    if (!seasonToStart) return;

    setLoading(true);
    try {
      // Build playerSelections array from autoTransitions and selectedPlayers
      const playerSelections = useRegistrationDivisions
        ? buildRegistrationSelections()
        : autoTransitions
        .filter((t) => selectedPlayers.includes(t.playerId))
        .map((t) => {
          const manualDivision = manualOverrides[t.playerId];
          return {
            playerId: t.playerId,
            playerName: t.playerName,
            currentDivision: t.currentDivision,
            newDivision: manualDivision || t.newDivision,
            transitionType: manualDivision ? "manual" : t.transitionType,
            shouldContinue: true,
          };
        });

      const response = await apiRequest(
        `/api/seasons/${seasonToStart.id}/preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            playerSelections,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        setTransitionPreview(data.transitions);
        setPreviewGenerated(true);
        setActiveStep(3); // Move to preview step
      } else {
        const data = await response.json();
        setError(data.error || "Failed to preview transitions");
      }
    } catch (error) {
      console.error("Error previewing transitions:", error);
      setError("Failed to preview transitions");
    } finally {
      setLoading(false);
    }
  };

  const handleStartSeason = async () => {
    if (!seasonToStart) return;

    setLoading(true);
    try {
      // Build playerSelections from the preview transitions, applying any manual overrides.
      // Also include selected players who have no transition entry (e.g. unassigned) with a "stays" entry.
      const transitionMap = new Map(transitionPreview.map((t) => [t.playerId, t]));

      const playerSelections = useRegistrationDivisions ? buildRegistrationSelections() : selectedPlayers.map((playerId) => {
        const t = transitionMap.get(playerId);
        const manualDivision = manualOverrides[playerId];
        if (t) {
          return {
            playerId: t.playerId,
            playerName: t.playerName,
            currentDivision: t.currentDivision,
            newDivision: manualDivision || t.newDivision,
            transitionType: manualDivision ? "manual" : t.transitionType,
            shouldContinue: true,
          };
        }
        // Player was selected but had no ranking entry — keep in same division
        const player = allPlayers.find((p) => p.id === playerId);
        return {
          playerId,
          playerName: player?.name || playerId,
          currentDivision: player?.division || "unassigned",
          newDivision: manualDivision || player?.division || "unassigned",
          transitionType: "stays",
          shouldContinue: true,
        };
      });

      const response = await apiRequest(
        `/api/seasons/${seasonToStart.id}/start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ playerSelections }),
        },
      );

      if (response.ok) {
        setSuccess("New season started successfully!");
        setWizardOpen(false);
        setSeasonToStart(null);
        setActiveStep(0);
        setSelectedPlayers([]);
        setManualOverrides({});
        setTransitionPreview([]);
        refreshSeasons();
      } else {
        const data = await response.json();
        setError(data.error || "Failed to start season");
      }
    } catch (error) {
      console.error("Error starting season:", error);
      setError("Failed to start season");
    } finally {
      setLoading(false);
    }
  };

  const togglePlayerSelection = (playerId: string) => {
    setSelectedPlayers((prev) =>
      prev.includes(playerId)
        ? prev.filter((id) => id !== playerId)
        : [...prev, playerId],
    );
  };

  const setManualDivision = (playerId: string, division: Division) => {
    setManualOverrides((prev) => ({
      ...prev,
      [playerId]: division,
    }));
  };

  const getStatusChip = (status: Season["status"]) => {
    switch (status) {
      case "active":
        return <Chip label="Active" color="success" size="small" />;
      case "completed":
        return <Chip label="Completed" color="default" size="small" />;
      case "draft":
        return <Chip label="Draft" color="warning" size="small" />;
      default:
        return null;
    }
  };

  const getTransitionIcon = (type: string) => {
    switch (type) {
      case "promoted":
        return <PromotionIcon sx={{ color: "#4caf50" }} />;
      case "relegated":
        return <RelegationIcon sx={{ color: "#f44336" }} />;
      case "manual":
        return <EditIcon sx={{ color: "#ff9800" }} />;
      default:
        return <TransferIcon sx={{ color: "#2196f3" }} />;
    }
  };

  const wizardSteps = [
    {
      label: "Select Players",
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Players who registered for the new season are pre-selected. 
            Unregistered players are shown in red and deselected by default.
          </Typography>
          {registeredPlayerIds.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {registeredPlayerIds.length} players registered for the new season.
              {allPlayers.filter(p => p.isActive && !registeredPlayerIds.includes(p.id)).length > 0 && 
                ` ${allPlayers.filter(p => p.isActive && !registeredPlayerIds.includes(p.id)).length} players did not register.`
              }
            </Alert>
          )}
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {allPlayers
              .filter((p) => p.isActive)
              .map((player) => {
                const hasRegistered = registeredPlayerIds.includes(player.id);
                return (
                  <ListItem 
                    key={player.id}
                    sx={{
                      bgcolor: !hasRegistered ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                      borderLeft: !hasRegistered ? '3px solid #f44336' : 'none',
                      mb: 0.5
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedPlayers.includes(player.id)}
                        onChange={() => togglePlayerSelection(player.id)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography 
                          sx={{ 
                            color: !hasRegistered ? '#f44336' : 'inherit',
                            fontWeight: !hasRegistered ? 'bold' : 'normal'
                          }}
                        >
                          {player.name}
                          {!hasRegistered && ' ⚠️'}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="body2">
                            {player.division} - {player.gamesPlayed || 0} games played
                          </Typography>
                          {!hasRegistered && (
                            <Typography 
                              component="span" 
                              variant="body2" 
                              sx={{ color: '#f44336', display: 'block', fontWeight: 'medium' }}
                            >
                              Did not register for new season
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
          </List>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2">
              Selected: {selectedPlayers.length} players
            </Typography>
            {registeredPlayerIds.length > 0 && (
              <Typography variant="body2" color="success.main">
                ✓ Registered: {allPlayers.filter(p => p.isActive && registeredPlayerIds.includes(p.id) && selectedPlayers.includes(p.id)).length}
              </Typography>
            )}
            {allPlayers.filter(p => p.isActive && !registeredPlayerIds.includes(p.id) && selectedPlayers.includes(p.id)).length > 0 && (
              <Typography variant="body2" color="warning.main">
                ⚠️ Not registered but selected: {allPlayers.filter(p => p.isActive && !registeredPlayerIds.includes(p.id) && selectedPlayers.includes(p.id)).length}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      label: "Auto Promotions & Relegations",
      content: (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={useRegistrationDivisions}
                onChange={(e) => setUseRegistrationDivisions(e.target.checked)}
              />
            }
            label="Use divisions assigned in registrations (skip auto promotions/relegations)"
          />
          {useRegistrationDivisions && (() => {
            const withReg = selectedPlayers.filter((id) => registrationDivisions[id]);
            const withoutReg = selectedPlayers.filter((id) => !registrationDivisions[id]);
            const changed = withReg.filter((id) => {
              const p = allPlayers.find((pl) => pl.id === id);
              return p && p.division !== registrationDivisions[id];
            });
            return (
              <Alert severity={withoutReg.length ? "warning" : "info"} sx={{ my: 2 }}>
                {withReg.length} selected player(s) will be placed in the division assigned in
                registrations ({changed.length} change division).
                {withoutReg.length > 0 &&
                  ` ${withoutReg.length} selected player(s) have no registration division and will follow the auto transition (or stay in their current division): ${withoutReg
                    .map((id) => allPlayers.find((pl) => pl.id === id)?.name || id)
                    .join(", ")}.`}
              </Alert>
            );
          })()}
          {!useRegistrationDivisions && (
          <Typography variant="body2" color="text.secondary" paragraph>
            Automatic promotions and relegations based on final standings.
          </Typography>
          )}
          {!useRegistrationDivisions && DIVISIONS.map((division) => {
            const divisionTransitions = autoTransitions.filter(
              (t) => t.currentDivision === division,
            );
            const promotions = divisionTransitions.filter(
              (t) => t.transitionType === "promoted",
            );
            const relegations = divisionTransitions.filter(
              (t) => t.transitionType === "relegated",
            );

            if (promotions.length === 0 && relegations.length === 0)
              return null;

            return (
              <Card key={division} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {division}
                  </Typography>

                  {promotions.length > 0 && (
                    <>
                      <Typography
                        variant="subtitle2"
                        color="success.main"
                        gutterBottom
                      >
                        Promotions ({promotions.length})
                      </Typography>
                      <List dense>
                        {promotions.map((t) => (
                          <ListItem key={t.playerId}>
                            <ListItemIcon>
                              <PromotionIcon sx={{ color: "#4caf50" }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${t.position}. ${t.playerName}`}
                              secondary={`${t.points} pts → ${t.newDivision}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}

                  {relegations.length > 0 && (
                    <>
                      <Typography
                        variant="subtitle2"
                        color="error.main"
                        gutterBottom
                        sx={{ mt: 1 }}
                      >
                        Relegations ({relegations.length})
                      </Typography>
                      <List dense>
                        {relegations.map((t) => (
                          <ListItem key={t.playerId}>
                            <ListItemIcon>
                              <RelegationIcon sx={{ color: "#f44336" }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${t.position}. ${t.playerName}`}
                              secondary={`${t.points} pts → ${t.newDivision}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ),
    },
    {
      label: "Manual Overrides",
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            {useRegistrationDivisions
              ? "Players are placed in their registration division. Override it for specific players if needed."
              : "Override automatic transitions for specific players if needed."}
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Player</TableCell>
                  <TableCell>Current Division</TableCell>
                  <TableCell>
                    {useRegistrationDivisions ? "Registration Division" : "Auto Transition"}
                  </TableCell>
                  <TableCell>Override Division</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {autoTransitions
                  .filter((t) => selectedPlayers.includes(t.playerId))
                  .map((transition) => (
                    <TableRow key={transition.playerId}>
                      <TableCell>{transition.playerName}</TableCell>
                      <TableCell>{transition.currentDivision}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {useRegistrationDivisions && registrationDivisions[transition.playerId] ? (
                            registrationDivisions[transition.playerId]
                          ) : (
                            <>
                              {getTransitionIcon(transition.transitionType)}
                              {transition.newDivision}
                              {useRegistrationDivisions && (
                                <Typography variant="caption" color="text.secondary">
                                  (no registration division)
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={manualOverrides[transition.playerId] || ""}
                            onChange={(e) =>
                              setManualDivision(
                                transition.playerId,
                                e.target.value as Division,
                              )
                            }
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>{useRegistrationDivisions ? "Use Registration" : "Use Auto"}</em>
                            </MenuItem>
                            {DIVISIONS.map((div) => (
                              <MenuItem key={div} value={div}>
                                {div}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ),
    },
    {
      label: "Preview",
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Review all transitions before starting the new season.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Total players: {selectedPlayers.length}
            </Typography>
            <Typography variant="body2">
              Transitions with manual overrides:{" "}
              {Object.keys(manualOverrides).length}
            </Typography>
          </Alert>

          {previewGenerated ? (
            transitionPreview.length > 0 ? (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Player</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transitionPreview.map((t) => {
                      const override = manualOverrides[t.playerId];
                      const finalDivision = override || t.newDivision;
                      const finalType = override ? "manual" : t.transitionType;

                      return (
                        <TableRow key={t.playerId}>
                          <TableCell>{t.playerName}</TableCell>
                          <TableCell>{t.currentDivision}</TableCell>
                          <TableCell>{finalDivision}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {getTransitionIcon(finalType)}
                              <Typography variant="caption">
                                {finalType}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                No promotions/relegations to apply — there's no prior ranking data for
                the selected players (e.g. this is the first season). All selected
                players will simply carry over to their current division.
              </Alert>
            )
          ) : (
            <Button
              variant="contained"
              onClick={handlePreviewTransitions}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Generate Preview"}
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box p={3}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3
        }}
      >
        <Typography variant="h4">Season Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          size="small"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Create Season
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            All Seasons
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Season Name</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Divisions</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allSeasons.map((season) => (
                  <TableRow key={season.id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {season.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {season.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(season.status)}</TableCell>
                    <TableCell>
                      <Tooltip title={(season.divisions || []).join(", ")}>
                        <Typography variant="body2">
                          {season.divisions?.length || 0} divisions
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {season.startDate &&
                          new Date(season.startDate).toLocaleDateString()}
                        {season.endDate &&
                          ` - ${new Date(season.endDate).toLocaleDateString()}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEditDates(season)}
                        >
                          Edit
                        </Button>
                        {season.status === "active" && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => {
                              setSeasonToComplete(season);
                              setCompleteDialogOpen(true);
                            }}
                          >
                            Complete
                          </Button>
                        )}
                        {season.status === "draft" && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => handleStartSeasonWizard(season)}
                            >
                              Start
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => {
                                setSeasonToDelete(season);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Season Dates Dialog */}
      <Dialog
        open={!!editDatesSeason}
        onClose={() => setEditDatesSeason(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Season: {editDatesSeason?.name}</DialogTitle>
        <DialogContent>
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            margin="normal"
            value={editStartDate}
            onChange={(e) => setEditStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            fullWidth
            margin="normal"
            value={editEndDate}
            onChange={(e) => setEditEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
            Divisions in this season
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {Array.from(new Set([...DIVISIONS, ...editDivisions])).map((div) => (
              <FormControlLabel
                key={div}
                control={
                  <Checkbox
                    checked={editDivisions.includes(div)}
                    onChange={(e) =>
                      setEditDivisions((prev) =>
                        e.target.checked ? [...prev, div] : prev.filter((d) => d !== div),
                      )
                    }
                    size="small"
                  />
                }
                label={div === "Women Division" ? "Women Division 1" : div}
                sx={{ width: "50%" }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDatesSeason(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDates} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Season Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Season</DialogTitle>
        <DialogContent>
          <TextField
            label="Season Name"
            fullWidth
            value={newSeasonName}
            onChange={(e) => setNewSeasonName(e.target.value)}
            placeholder="e.g., Summer 2026"
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            label="Season ID"
            fullWidth
            value={newSeasonId}
            onChange={(e) => setNewSeasonId(e.target.value)}
            placeholder="e.g., summer-26"
            helperText="Lowercase, use hyphens instead of spaces"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            value={newSeasonStartDate}
            onChange={(e) => setNewSeasonStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="End Date"
            type="date"
            fullWidth
            value={newSeasonEndDate}
            onChange={(e) => setNewSeasonEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
            Divisions in this season
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0 }}>
            {DIVISIONS.map((div) => (
              <FormControlLabel
                key={div}
                control={
                  <Checkbox
                    checked={newSeasonDivisions.includes(div)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewSeasonDivisions((prev) => [...prev, div]);
                      } else {
                        setNewSeasonDivisions((prev) => prev.filter((d) => d !== div));
                      }
                    }}
                    size="small"
                  />
                }
                label={div === "Women Division" ? "Women Division 1" : div}
                sx={{ width: "50%" }}
              />
            ))}
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            The season will be created as a draft. You can start it later to
            activate it and transition players.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSeason}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Draft Season Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Draft Season</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{seasonToDelete?.name}</strong>?
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            This action cannot be undone. The draft season and all its data will be permanently deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!seasonToDelete) return;
              setLoading(true);
              try {
                const response = await apiRequest(`/api/seasons/${seasonToDelete.id}`, {
                  method: "DELETE",
                });
                if (response.ok) {
                  setSuccess(`Season "${seasonToDelete.name}" deleted.`);
                  setDeleteDialogOpen(false);
                  setSeasonToDelete(null);
                  refreshSeasons();
                } else {
                  const data = await response.json();
                  setError(data.error || "Failed to delete season");
                }
              } catch (e) {
                setError("Failed to delete season");
              } finally {
                setLoading(false);
              }
            }}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Season Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
      >
        <DialogTitle>Complete Season</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to complete {seasonToComplete?.name}?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will mark the season as completed and prevent further changes.
            Make sure all league days and matches are finalized.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCompleteSeason}
            variant="contained"
            color="warning"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Complete Season"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start Season Wizard */}
      <Dialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Start Season: {seasonToStart?.name}</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {wizardSteps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  {step.content}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep(activeStep + 1)}
                      sx={{ mr: 1 }}
                      disabled={index === wizardSteps.length - 1}
                    >
                      {index === wizardSteps.length - 1 ? "Finish" : "Continue"}
                    </Button>
                    {activeStep > 0 && (
                      <Button onClick={() => setActiveStep(activeStep - 1)}>
                        Back
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWizardOpen(false)}>Cancel</Button>
          {activeStep === wizardSteps.length - 1 && (
            <Button
              onClick={handleStartSeason}
              variant="contained"
              color="success"
              disabled={loading || !previewGenerated || selectedPlayers.length === 0}
            >
              {loading ? <CircularProgress size={24} /> : "Start Season"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SeasonManagement;
